// Generated by tutorials/record.cjs from tutorials/articles/h49-menu-analysis.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h49 --text-only` (wording) or
// `node tutorials/record.cjs h49` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'menu-analysis',
  catalogId: 'h49',
  title: 'Menu Analysis',
  summary: 'Check a menu cycle\'s nutrition before you serve it: what each diet order gets every day, in calories, protein, sodium and more, compared with the daily targets.',
  audience: 'Menu planners and dietitians who check that a menu meets nutrition targets',
  area: 'menus',
  learn: [
    'Open the nutrition analysis for a menu cycle',
    'Choose the diet orders, combinations and nutrients to check',
    'Run the analysis for the whole cycle or one week',
    'Read the daily averages and spot the days off target',
  ],
  keywords: [
    'menu analysis',
    'nutrition analysis',
    'nutrient analysis',
    'nutrition',
    'calories',
    'protein',
    'sodium',
    'carbohydrate',
    'dri',
    'targets',
    'diet order',
    'combination',
    'menu cycle',
    'dietitian',
  ],
  steps: [
    {
      id: 'open',
      part: 'Open the analysis',
      heading: 'Open Nutrition Analysis',
      text: 'On Menu Cycles, select the cycle, then Modify. In the menu cycle, select Tools, then Nutrition Analysis (per diet order).\n\nThe analysis opens for that cycle, with its name at the top.',
      media: {
        image: {
          src: '/help/menu-analysis/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/menu-analysis/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The menu cycle Fall/Winter Cycle 2026 open with the Tools menu showing Nutrition Analysis (per diet order).',
      },
    },
    {
      id: 'diet-orders',
      part: 'Choose what to check',
      heading: 'Choose the diet orders',
      text: 'Under Diet orders, every diet type is chosen to start with. Select one to leave it out, and select it again to put it back.',
      media: {
        image: {
          src: '/help/menu-analysis/diet-orders.webp',
          width: 1280,
          height: 300,
        },
        clip: {
          src: '/help/menu-analysis/diet-orders.mp4',
          width: 1280,
          height: 300,
          durationSec: 2.1,
        },
        alt: 'What to analyse, with every diet type chosen except Liberal Geriatric.',
      },
    },
    {
      id: 'combination',
      part: 'Choose what to check',
      heading: 'Add a combination',
      text: 'To check a diet with a texture, pick the diet and the consistency under Combinations, then select Add. Select a combination to remove it.',
      media: {
        image: {
          src: '/help/menu-analysis/combination.webp',
          width: 1280,
          height: 300,
        },
        clip: {
          src: '/help/menu-analysis/combination.mp4',
          width: 1280,
          height: 300,
          durationSec: 6,
        },
        alt: 'The combination Renal + Pureed added under Combinations.',
      },
    },
    {
      id: 'nutrients',
      part: 'Choose what to check',
      heading: 'Choose the nutrients, targets and days',
      text: 'Under Nutrients, select the ones to check. DRI group sets the daily targets; leave it on your facility\'s default unless you are checking for another group.\n\nPick All 28 days for the whole cycle, or Week 1 for a quicker look.',
      media: {
        image: {
          src: '/help/menu-analysis/nutrients.webp',
          width: 1280,
          height: 300,
        },
        clip: {
          src: '/help/menu-analysis/nutrients.mp4',
          width: 1280,
          height: 300,
          durationSec: 2.5,
        },
        alt: 'Nutrients with Calories, Protein, Carbohydrate, Sodium and Fiber chosen, DRI group Adult 71+ and All 28 days.',
      },
    },
    {
      id: 'run',
      part: 'Choose what to check',
      heading: 'Run the analysis',
      text: 'Select Analyse. Every day and meal is worked out for each diet order; the bar shows how far it has got. A whole cycle can take a few minutes, and you can keep reading the results as they come in.',
      media: {
        image: {
          src: '/help/menu-analysis/run.webp',
          width: 1280,
          height: 336,
        },
        clip: {
          src: '/help/menu-analysis/run.mp4',
          width: 1280,
          height: 336,
          durationSec: 4.2,
        },
        alt: 'The Analyse 7 diet orders button and the progress bar reading Done: 588 meals.',
      },
    },
    {
      id: 'summary',
      part: 'Read the results',
      heading: 'Read the summary',
      text: 'For the nutrient picked at the top, each diet order shows its daily average, its lowest and highest day, the daily target, and how many days are under or over it.\n\nStart with the diet orders that have days off target.',
      media: {
        image: {
          src: '/help/menu-analysis/summary.webp',
          width: 1280,
          height: 242,
        },
        alt: 'Calories per diet order: daily averages close to 2,000 kcal against a target of 1,800 to 2,200, with a few days under for Consistent Carbohydrate and Renal + Pureed.',
      },
    },
    {
      id: 'by-day',
      part: 'Read the results',
      heading: 'Find the days off target',
      text: 'Pick another nutrient at the top, such as Sodium. The grid below shows each diet order day by day, with the days under or over the target marked. Point at a day to see its value and target.\n\nHere Regular is over the sodium limit most days, while Low Sodium 2g stays under it.',
      media: {
        image: {
          src: '/help/menu-analysis/by-day.webp',
          width: 1280,
          height: 588,
        },
        clip: {
          src: '/help/menu-analysis/by-day.mp4',
          width: 1280,
          height: 588,
          durationSec: 3.4,
        },
        alt: 'Sodium per diet order and the day-by-day grid, with Regular marked over the 2,300 mg limit on most of its 28 days and Low Sodium 2g under it every day.',
      },
    },
    {
      id: 'back-to-menu',
      part: 'Read the results',
      heading: 'Change the menu',
      text: 'To fix a day, select the grid button at the top right to open the cycle in Modify, change the items on that day, then run the analysis again.',
      media: {
        image: {
          src: '/help/menu-analysis/back-to-menu.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The top of the Nutrition analysis page for Fall/Winter Cycle 2026, with the Open the menu in Modify button pointed at.',
      },
    },
  ],
  tips: [
    'Run Week 1 first for a quick check while you are still changing the menu, then all days when it is finished.',
    'Add the combinations your residents actually have, such as Renal with Pureed. A texture can change what is served, and so the numbers.',
    'When the analysis is done, Draft a summary can write a plain-language summary of the numbers for the dietitian to review. It uses only the numbers on the page, never resident information.',
  ],
  notes: [
    'Each day is breakfast, lunch and dinner for one resident on that diet order, built the same way as tray tickets. Always Served items and nourishments are not included.',
    'The daily target comes from the DRI group you pick; Facility default uses your facility\'s own. A nutrient with no target shows none set.',
    'A day marked incomplete had a meal that could not be worked out, for example a row with no item. Check that day in the menu.',
  ],
  related: ['reading-a-menu-cycle', 'changing-menu-items', 'diet-and-consistency-rules', 'menu-cycles'],
  recorded: '2026-10-26',
};

export default article;
